import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";

/**
 * Close Operation modal — UI-consistency rewrite (reactstrap).
 *
 * Behavior identical to the previous custom-overlay version:
 *   - Validates Pass / Fail are non-negative integers
 *   - Calls onConfirm({ pass, fail }) when valid
 *   - Calls onCancel on dismiss
 *
 * Legacy parity: matches CloseOp.vb (txtPass + txtFail required as integers).
 */
const CloseOpModal = ({
  open,
  onCancel,
  onConfirm,
  jobNum,
  opNum,
  totalPieces,
}) => {
  const [passCount, setPassCount] = useState("");
  const [failCount, setFailCount] = useState("");

  useEffect(() => {
    if (!open) {
      setPassCount("");
      setFailCount("");
    }
  }, [open]);

  const valid = useMemo(() => {
    const p = Number(passCount);
    const f = Number(failCount);
    return Number.isInteger(p) && Number.isInteger(f) && p >= 0 && f >= 0;
  }, [passCount, failCount]);

  const handlePassChange = useCallback((e) => setPassCount(e.target.value), []);
  const handleFailChange = useCallback((e) => setFailCount(e.target.value), []);
  const handleConfirm = useCallback(() => {
    onConfirm?.({ pass: Number(passCount), fail: Number(failCount) });
  }, [onConfirm, passCount, failCount]);

  return (
    <Modal isOpen={!!open} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        <i className="ri-stop-circle-line align-bottom me-2 text-primary"></i>
        Close Operation
      </ModalHeader>
      <ModalBody>
        <div className="text-muted small mb-3">
          <div>
            <strong>Job:</strong> {jobNum || "—"}
          </div>
          <div>
            <strong>Operation #:</strong> {opNum ?? "—"}
          </div>
          {typeof totalPieces === "number" && (
            <div>
              <strong>Total Pieces:</strong> {totalPieces}
            </div>
          )}
        </div>
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Label for="close-pass">#&nbsp;Pass</Label>
                <Input
                  id="close-pass"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={passCount}
                  onChange={handlePassChange}
                  placeholder="e.g., 10"
                  autoFocus
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Label for="close-fail">#&nbsp;Fail</Label>
                <Input
                  id="close-fail"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={failCount}
                  onChange={handleFailChange}
                  placeholder="e.g., 2"
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" disabled={!valid} onClick={handleConfirm}>
          <i className="ri-check-line align-bottom me-1"></i>
          Close Operation
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(CloseOpModal);
